import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { db } from "./firebaseConfig";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import LearningContentBuilder from "./LearningContentBuilder";
import { quizHasNoTimeLimit } from "./utils/assignmentTiming";
import { normalizeLearningContentBlocks, validateLearningContentDocument } from "./utils/learningContent";
import {
  clearLearningContentApi,
  saveLearningContentApi,
} from "./utils/learningContentApi";
import {
  cleanupUnreferencedUploadedMedia,
  loadLearningContentMediaBlobUrl,
  uploadLearningContentMediaDrafts,
} from "./utils/learningContentMediaApi";
import QuizMcqPreview from "./components/QuizMcqPreview";
import AssessmentOutcomesEditor from "./components/AssessmentOutcomesEditor";
import {
  buildAssessmentOutcomeConfig,
  defaultAssessmentOutcomeConfig,
  normalizeAssessmentOutcomeConfig,
} from "./utils/assessmentOutcomeConfig";
import {
  calculateQuizTotalMarks,
  formatQuizMarkCount,
  quizQuestionValidationMessage,
  validateQuizQuestionSelection,
} from "./utils/quizQuestionCompatibility";
import "./EditQuiz.css";

// ─── Natural sort helper (same logic as CreateQuestions.js) ──────────────────
// Used to sort the separate Question Bank browsing list.
const naturalSortByLabel = (labelA, labelB) => {
  const normalize = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    return raw
      .trim()
      .replace(/^question\s+/i, '')
      .replace(/^q0*/i, '');
  };
  const tokenize = (s) => normalize(s).split(/(\d+)/);
  const tokA = tokenize(labelA);
  const tokB = tokenize(labelB);
  const len = Math.max(tokA.length, tokB.length);
  for (let i = 0; i < len; i++) {
    const a = tokA[i] ?? '';
    const b = tokB[i] ?? '';
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);
    if (!isNaN(na) && !isNaN(nb)) {
      if (na !== nb) return na - nb;
    } else {
      const cmp = a.localeCompare(b, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
};

// Promise.all preserves the order of the stored questionIds array, so mapping
// the returned snapshots directly restores the quiz's authoritative order.
export const questionsInStoredQuizOrder = (snapshots) =>
  snapshots
    .filter((snapshot) => snapshot.exists())
    .map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }));

export const orderedQuestionIdsEqual = (left, right) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

// ─── styles ────────────────────────────────────────────────────────────────────


// ─── component ─────────────────────────────────────────────────────────────────

const EditQuiz = ({ user }) => {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null); // { type: "success"|"error", text }

  // ── quiz settings state
  const [quizTitle, setQuizTitle] = useState("");
  const [defaultTimeLimit, setDefaultTimeLimit] = useState(60); // in minutes
  // A quiz stored without a time limit (missing, null or 0 seconds) has no
  // timer. It stays that way unless the teacher enters a duration.
  const [storedUntimed, setStoredUntimed] = useState(false);
  const [allowPaste, setAllowPaste] = useState(false);
  const [locked, setLocked] = useState(false);
  const [status, setStatus] = useState("active");
  const [assessmentOutcomeConfig, setAssessmentOutcomeConfig] = useState(
    defaultAssessmentOutcomeConfig
  );

  // ── questions list (sorted by order)
  const [questions, setQuestions] = useState([]);
  const [originalQuestionIds, setOriginalQuestionIds] = useState([]);
  const [quizHasAssignments, setQuizHasAssignments] = useState(false);
  const [quizCodeDisplay, setQuizCodeDisplay] = useState(""); // read-only display

  // Learning content is loaded and saved independently from quiz settings/questions.
  const [quizHasLearningContent, setQuizHasLearningContent] = useState(false);
  const [learningContentBlocks, setLearningContentBlocks] = useState([]);
  const [learningContentLoadError, setLearningContentLoadError] = useState("");
  const [showLearningContentBuilder, setShowLearningContentBuilder] = useState(false);
  const [contentSaving, setContentSaving] = useState(false);
  const [contentMessage, setContentMessage] = useState(null);
  const [learningContentMediaDrafts, setLearningContentMediaDrafts] = useState({});
  const [learningContentMediaPreviews, setLearningContentMediaPreviews] = useState({});
  const mediaUrlsRef = useRef({ drafts: learningContentMediaDrafts, previews: learningContentMediaPreviews });

  useEffect(() => {
    mediaUrlsRef.current = { drafts: learningContentMediaDrafts, previews: learningContentMediaPreviews };
  }, [learningContentMediaDrafts, learningContentMediaPreviews]);

  useEffect(() => () => {
    const urls = [
      ...Object.values(mediaUrlsRef.current.drafts).map((draft) => draft?.previewUrl),
      ...Object.values(mediaUrlsRef.current.previews).map((preview) => preview?.url),
    ];
    [...new Set(urls.filter(Boolean))].forEach((url) => URL.revokeObjectURL(url));
  }, []);

  // ── Question Bank modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSearch, setBankSearch] = useState("");
  const [bankFilters, setBankFilters] = useState({ subject: "", topic: "", level: "", specCode: "" });
  const [selectedBankIds, setSelectedBankIds] = useState(new Set());
  const totalQuizMarks = useMemo(() => calculateQuizTotalMarks(questions), [questions]);

  // ── load quiz on mount
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const quizSnap = await getDoc(doc(db, "quizzes", quizId));
        if (!quizSnap.exists()) {
          alert("Test not found.");
          navigate("/view-quizzes");
          return;
        }
        const data = quizSnap.data();

        setQuizTitle(data.quizTitle || data.quizCode || "");
        setQuizCodeDisplay(data.quizCode || quizId);
        const untimed = quizHasNoTimeLimit(data.defaultTimeLimit);
        setStoredUntimed(untimed);
        setDefaultTimeLimit(untimed ? "" : Math.round(data.defaultTimeLimit / 60)); // Convert seconds to minutes
        setAllowPaste(data.allowPaste === true);
        setLocked(data.locked === true);
        setStatus(data.status || "active");
        setAssessmentOutcomeConfig(
          normalizeAssessmentOutcomeConfig(data.assessmentOutcomeConfig)
        );
        setQuizHasAssignments(data.hasAssignments === true);

        setQuizHasLearningContent(data.hasLearningContent === true);
        setLearningContentBlocks([]);
        setLearningContentMediaDrafts({});
        setLearningContentMediaPreviews({});
        setLearningContentLoadError("");
        if (data.hasLearningContent === true) {
          try {
            const contentSnap = await getDoc(
              doc(db, "quizzes", quizId, "learningContent", "main")
            );
            if (!contentSnap.exists()) {
              throw new Error(
                "The test says learning content exists, but its content document is missing."
              );
            }
            const normalizedContent = validateLearningContentDocument(
              contentSnap.data(),
              {
                expectedQuizId: quizId,
                expectedOwnerUid: data.creatorUid,
              }
            );
            setLearningContentBlocks(normalizedContent.blocks);
            const previews = {};
            await Promise.all(
              normalizedContent.blocks
                .filter((block) => block.type === "image" || block.type === "resource")
                .map(async (block) => {
                  try {
                    previews[block.id] = {
                      status: "loaded",
                      url: await loadLearningContentMediaBlobUrl(block.storagePath),
                    };
                  } catch (previewError) {
                    console.error("[EditQuiz] media preview load error:", block.id, previewError);
                    previews[block.id] = { status: "failed", url: "", error: "Preview could not be loaded." };
                  }
                })
            );
            setLearningContentMediaPreviews(previews);
          } catch (contentError) {
            console.error("[EditQuiz] learning content load error:", contentError);
            setLearningContentLoadError(
              `Learning content could not be loaded safely: ${contentError.message}`
            );
          }
        }

        // Load individual question documents
        const ids = Array.isArray(data.questionIds) ? [...data.questionIds] : [];
        setOriginalQuestionIds(ids);
        if (ids.length > 0) {
          const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, "questions", id))));
          const loaded = questionsInStoredQuizOrder(snaps);
          setQuestions(loaded);
        } else {
          setQuestions([]);
        }
      } catch (err) {
        console.error("[EditQuiz] load error:", err);
        alert("Failed to load test: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (quizId) load();
  }, [quizId, navigate]);

  // ── load the teacher's Question Bank
  const loadQuestionBank = useCallback(async () => {
    setBankLoading(true);
    setBankQuestions([]);
    try {
      const q = user?.role === "admin"
        ? collection(db, "questions")
        : query(collection(db, "questions"), where("createdBy", "==", user.uid));
      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => naturalSortByLabel(a.questionLabel || "", b.questionLabel || ""));
      setBankQuestions(all);
    } catch (err) {
      console.error("[EditQuiz] loadQuestionBank error:", err);
    } finally {
      setBankLoading(false);
    }
  }, [user]);

  // ── open the modal and load bank if needed
  const openAddModal = useCallback(() => {
    setShowAddModal(true);
    setBankSearch("");
    setBankFilters({ subject: "", topic: "", level: "", specCode: "" });
    setSelectedBankIds(new Set());
    loadQuestionBank();
  }, [loadQuestionBank]);

  // ── toggle a question selection in the bank modal
  const toggleBankSelect = useCallback((id) => {
    setSelectedBankIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // ── add selected bank questions to the quiz (no duplicates)
  const handleAddToQuiz = useCallback(() => {
    if (selectedBankIds.size === 0) return;
    const currentIds = new Set(questions.map((q) => q.id));
    const toAdd = bankQuestions.filter(
      (q) => selectedBankIds.has(q.id) && !currentIds.has(q.id)
    );
    if (toAdd.length === 0) {
      alert("All selected questions are already in this test.");
      return;
    }
    const skipped = selectedBankIds.size - toAdd.length;
    setQuestions((prev) => [...prev, ...toAdd]);
    setSelectedBankIds(new Set());
    setShowAddModal(false);
    if (skipped > 0) {
      setSaveMessage({
        type: "success",
        text: `✅ ${toAdd.length} question${toAdd.length !== 1 ? "s" : ""} added. ${skipped} already present — skipped. Click Save Changes to persist.`,
      });
    } else {
      setSaveMessage({
        type: "success",
        text: `✅ ${toAdd.length} question${toAdd.length !== 1 ? "s" : ""} added. Click Save Changes to persist.`,
      });
    }
    setTimeout(() => setSaveMessage(null), 5000);
  }, [selectedBankIds, questions, bankQuestions]);

  // ── remove a question from the quiz (does NOT delete from Firestore)
  const handleRemoveFromQuiz = useCallback((questionId) => {
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
  }, []);

  // ── filtered bank questions for the modal
  const filteredBankQuestions = useMemo(() => {
    const sl = bankSearch.toLowerCase();
    return bankQuestions.filter((q) => {
      const searchMatch =
        !bankSearch ||
        (q.questionLabel || "").toLowerCase().includes(sl) ||
        (q.questionText || "").toLowerCase().includes(sl) ||
        (q.topic || "").toLowerCase().includes(sl) ||
        (q.subject || "").toLowerCase().includes(sl);
      const subjectMatch = !bankFilters.subject || q.subject === bankFilters.subject;
      const topicMatch = !bankFilters.topic || (q.topic || "").toLowerCase().includes(bankFilters.topic.toLowerCase());
      const levelMatch = !bankFilters.level || (q.level || "").toLowerCase().includes(bankFilters.level.toLowerCase());
      const specMatch = !bankFilters.specCode || (q.specCode || "").toLowerCase().includes(bankFilters.specCode.toLowerCase());
      return searchMatch && subjectMatch && topicMatch && levelMatch && specMatch;
    });
  }, [bankQuestions, bankSearch, bankFilters]);

  // ── unique subjects for the subject filter dropdown
  const bankSubjects = useMemo(() => {
    return [...new Set(bankQuestions.map((q) => q.subject).filter(Boolean))].sort();
  }, [bankQuestions]);

  // ── current quiz question IDs (for duplicate detection in modal)
  const currentQuizIds = useMemo(() => new Set(questions.map((q) => q.id)), [questions]);

  // ── move question up
  const moveUp = useCallback((idx) => {
    if (idx === 0) return;
    setQuestions((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }, []);

  // ── move question down
  const moveDown = useCallback((idx) => {
    setQuestions((prev) => {
      if (idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, []);

  // ── save all changes
  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      // Validate time limit. Left blank on an untimed quiz, it is not written,
      // so the quiz keeps exactly the value it had.
      const keepUntimed = storedUntimed && String(defaultTimeLimit).trim() === "";
      const timeLimitNum = Number(defaultTimeLimit);
      if (!keepUntimed && (isNaN(timeLimitNum) || timeLimitNum < 1 || timeLimitNum > 180)) {
        setSaveMessage({ type: "error", text: "❌ Please enter a valid time limit between 1 and 180 minutes." });
        setSaving(false);
        return;
      }

      const questionValidation = validateQuizQuestionSelection(questions);
      if (!questionValidation.isValid) {
        setSaveMessage({
          type: "error",
          text: quizQuestionValidationMessage(questionValidation),
        });
        return;
      }

      let cleanAssessmentOutcomeConfig;
      try {
        cleanAssessmentOutcomeConfig = buildAssessmentOutcomeConfig(assessmentOutcomeConfig);
      } catch (error) {
        setSaveMessage({ type: "error", text: `Assessment Outcomes: ${error.message}` });
        return;
      }

      const currentQuestionIds = questions.map((question) => question.id);
      const questionStructureChanged = !orderedQuestionIdsEqual(
        originalQuestionIds,
        currentQuestionIds
      );

      if (questionStructureChanged && quizHasAssignments) {
        setSaveMessage({
          type: "error",
          text: "This test has already been assigned. Questions cannot be added, removed, or reordered.",
        });
        return;
      }

      const batch = writeBatch(db);

      // 1. Always persist editable metadata. Question structure is written only
      // when it actually changed on an unassigned quiz.
      const quizRef = doc(db, "quizzes", quizId);
      const quizUpdate = {
        quizTitle: quizTitle.trim() || quizCodeDisplay,
        ...(keepUntimed ? {} : { defaultTimeLimit: timeLimitNum * 60 }), // Convert minutes to seconds
        allowPaste: allowPaste,
        locked: locked,
        assessmentOutcomeConfig: cleanAssessmentOutcomeConfig,
      };
      if (questionStructureChanged) quizUpdate.questionIds = currentQuestionIds;
      batch.update(quizRef, quizUpdate);

      // 2. Question order writes are structural and unnecessary for metadata-only saves.
      if (questionStructureChanged) {
        questions.forEach((q, idx) => {
          const qRef = doc(db, "questions", q.id);
          batch.update(qRef, { order: idx + 1 });
        });
      }

      await batch.commit();

      if (questionStructureChanged) setOriginalQuestionIds(currentQuestionIds);

      setSaveMessage({ type: "success", text: "✅ Test saved successfully!" });
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
      setTimeout(() => setSaveMessage(null), 4000);
    } catch (err) {
      console.error("[EditQuiz] save error:", err);
      setSaveMessage({ type: "error", text: "❌ Failed to save: " + err.message });
    } finally {
      setSaving(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  const handleClearLearningContent = async () => {
    if (!quizHasLearningContent || contentSaving) return;
    const confirmed = window.confirm(
      "Remove learning content?\n\nThis removes the optional learning page from this test. The test and its questions will not be changed."
    );
    if (!confirmed) return;

    setContentSaving(true);
    setContentMessage(null);
    try {
      await clearLearningContentApi(quizId);
      await cleanupUnreferencedUploadedMedia(quizId, [], learningContentMediaDrafts);
      Object.values(learningContentMediaPreviews).forEach((preview) => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
      });
      setLearningContentBlocks([]);
      setLearningContentMediaDrafts({});
      setLearningContentMediaPreviews({});
      setQuizHasLearningContent(false);
      setShowLearningContentBuilder(false);
      setContentMessage({ type: "success", text: "Learning content removed." });
    } catch (error) {
      setContentMessage({
        type: "error",
        text: error.message || "Learning content could not be removed.",
      });
    } finally {
      setContentSaving(false);
    }
  };

  const handleSaveLearningContent = async (normalizedBlocks) => {
    setContentMessage(null);
    if (normalizedBlocks.length === 0) {
      if (quizHasLearningContent) {
        await handleClearLearningContent();
      } else {
        setShowLearningContentBuilder(false);
      }
      return;
    }

    setContentSaving(true);
    try {
      const uploadResult = await uploadLearningContentMediaDrafts({
        quizId,
        blocks: normalizedBlocks,
        mediaDrafts: learningContentMediaDrafts,
        onProgress: (nextBlocks, nextDrafts) => {
          setLearningContentBlocks(nextBlocks);
          setLearningContentMediaDrafts(nextDrafts);
        },
      });
      const persistedBlocks = normalizeLearningContentBlocks(uploadResult.blocks);
      const result = await saveLearningContentApi(quizId, persistedBlocks);
      await cleanupUnreferencedUploadedMedia(quizId, persistedBlocks, uploadResult.mediaDrafts);
      setLearningContentBlocks(persistedBlocks);
      setLearningContentMediaDrafts(uploadResult.mediaDrafts);
      setLearningContentMediaPreviews((current) => {
        const referencedIds = new Set(persistedBlocks.map((block) => block.id));
        const next = { ...current };
        Object.entries(current).forEach(([blockId, preview]) => {
          if (!referencedIds.has(blockId) || uploadResult.mediaDrafts[blockId]?.previewUrl) {
            if (preview?.url) URL.revokeObjectURL(preview.url);
            delete next[blockId];
          }
        });
        return next;
      });
      setQuizHasLearningContent(true);
      setShowLearningContentBuilder(false);
      setContentMessage({
        type: "success",
        text: `Learning content saved (revision ${result.revision}).`,
      });
    } catch (error) {
      if (error.partialBlocks) setLearningContentBlocks(error.partialBlocks);
      if (error.partialMediaDrafts) setLearningContentMediaDrafts(error.partialMediaDrafts);
      setContentMessage({
        type: "error",
        text: error.message || "Learning content could not be saved.",
      });
    } finally {
      setContentSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="edit-quiz-page edit-quiz-loading-page" aria-busy="true">
        <section
          className="edit-quiz-loading-panel"
          role="status"
          aria-live="polite"
          aria-labelledby="edit-quiz-loading-title"
          aria-describedby="edit-quiz-loading-description"
        >
          <div className="edit-quiz-loading-indicator" aria-hidden="true">
            <span className="edit-quiz-loading-ring" />
            <span className="edit-quiz-loading-core" />
          </div>
          <p className="edit-quiz-eyebrow">Assessment editor</p>
          <h1 id="edit-quiz-loading-title">Loading your test</h1>
          <p id="edit-quiz-loading-description">
            We’re getting your assessment ready to edit.
          </p>
        </section>
      </main>
    );
  }

  if (showLearningContentBuilder) {
    return (
      <main className="edit-quiz-page learning-content-edit-page">
        <div className="edit-quiz-shell edit-quiz-shell--builder">
          <LearningContentBuilder
            blocks={learningContentBlocks}
            onChange={setLearningContentBlocks}
            mediaDrafts={learningContentMediaDrafts}
            onMediaDraftsChange={setLearningContentMediaDrafts}
            mediaPreviews={learningContentMediaPreviews}
            onBack={() => setShowLearningContentBuilder(false)}
            onSave={handleSaveLearningContent}
            onClear={quizHasLearningContent ? handleClearLearningContent : undefined}
            saving={contentSaving}
            saveError={contentMessage?.type === "error" ? contentMessage.text : ""}
            existingContent={quizHasLearningContent}
            saveLabel="Save Learning Content"
          />
        </div>
      </main>
    );
  }

  return (
    <main className="edit-quiz-page">
      <div className="edit-quiz-shell" inert={showAddModal}>
        {/* ── Header ── */}
        <header className="edit-quiz-header">
          <div>
            <p className="edit-quiz-eyebrow">Assessment editor</p>
            <h1>Edit Test</h1>
            <p className="edit-quiz-header-copy">
              Refine the settings, learning material and question sequence for this assessment.
            </p>
          </div>
          <button className="edit-quiz-button edit-quiz-button--secondary" onClick={() => navigate("/view-quizzes")}>
            ← Back to Tests
          </button>
        </header>

        {/* ── Save message ── */}
        {saveMessage && (
          <div
            className={`edit-quiz-notice edit-quiz-notice--${saveMessage.type}`}
            role={saveMessage.type === "error" ? "alert" : "status"}
            aria-live={saveMessage.type === "error" ? "assertive" : "polite"}
          >
            {saveMessage.text}
          </div>
        )}

        {/* ══ QUIZ SETTINGS ══════════════════════════════════════════════════ */}
        <section className="edit-quiz-section" aria-labelledby="edit-quiz-settings-title">
          <div className="edit-quiz-section-heading">
            <div>
              <p className="edit-quiz-section-kicker">Core details</p>
              <h2 id="edit-quiz-settings-title">Test Settings</h2>
            </div>
            <p>Manage how this assessment appears and behaves for students.</p>
          </div>

        <div className="edit-quiz-form-grid">
          {/* Test Title */}
          <div className="edit-quiz-field">
            <label htmlFor="edit-quiz-title">Test Title</label>
            <input
              id="edit-quiz-title"
              type="text"
              value={quizTitle}
              onChange={(e) => setQuizTitle(e.target.value)}
              placeholder="e.g. Pearson GCSE Computer Science Paper 2"
            />
            <span className="edit-quiz-field-hint">Displayed to teachers in test lists and reports.</span>
          </div>

          {/* Join Code — read-only */}
          <div className="edit-quiz-field">
            <label htmlFor="edit-quiz-code">Student Join Code</label>
            <input
              id="edit-quiz-code"
              className="edit-quiz-input--readonly"
              type="text"
              value={quizCodeDisplay}
              readOnly
            />
            <span className="edit-quiz-field-hint">
              The join code cannot be changed here. Students use this to enter the test.
            </span>
          </div>

          {/* Time Limit */}
          <div className="edit-quiz-field">
            <label htmlFor="edit-quiz-time-limit">Time Limit (minutes)</label>
            <input
              id="edit-quiz-time-limit"
              type="number"
              value={defaultTimeLimit}
              onChange={(e) => setDefaultTimeLimit(e.target.value)}
              placeholder={storedUntimed ? "No time limit" : "e.g. 60"}
              min="1"
              max="180"
            />
            <span className="edit-quiz-field-hint">
              {storedUntimed
                ? "This test has no time limit. Leave this blank to keep it untimed, or enter 1-180 minutes to add one."
                : "Duration allowed for students to complete the test (1-180 minutes)."}
            </span>
          </div>

        </div>

        {/* Toggles */}
        <div className="edit-quiz-toggle-grid">
          {/* Allow Paste */}
          <button
            type="button"
            className={`edit-quiz-toggle ${allowPaste ? "edit-quiz-toggle--active" : ""}`}
            role="switch"
            aria-checked={allowPaste}
            onClick={() => setAllowPaste((v) => !v)}
          >
            <span className="edit-quiz-toggle-track" aria-hidden="true">
              <span className="edit-quiz-toggle-knob" />
            </span>
            <span className="edit-quiz-toggle-copy">
              <strong>Allow Paste</strong>
              <span>Enable for programming or coding tests so students can paste code from an IDE.</span>
            </span>
          </button>

          {/* Locked */}
          <button
            type="button"
            className={`edit-quiz-toggle ${locked ? "edit-quiz-toggle--active" : ""}`}
            role="switch"
            aria-checked={locked}
            onClick={() => setLocked((v) => !v)}
          >
            <span className="edit-quiz-toggle-track" aria-hidden="true">
              <span className="edit-quiz-toggle-knob" />
            </span>
            <span className="edit-quiz-toggle-copy">
              <strong>Lock Test</strong>
              <span>Locked tests prevent students from submitting new answers.</span>
            </span>
          </button>
        </div>
        </section>

        <AssessmentOutcomesEditor
          value={assessmentOutcomeConfig}
          onChange={setAssessmentOutcomeConfig}
          teacherUid={user?.uid}
        />

        <section className="edit-quiz-section edit-quiz-learning-section" aria-labelledby="learning-content-management-title">
          <div className="edit-quiz-learning-layout">
            <div className="edit-quiz-learning-copy">
              <div className="edit-quiz-section-heading edit-quiz-section-heading--compact">
                <div>
                  <p className="edit-quiz-section-kicker">Optional preparation</p>
                  <h2 id="learning-content-management-title">Learning Content</h2>
                </div>
                <span
                  className={`edit-quiz-status-badge ${
                    learningContentLoadError
                      ? "edit-quiz-status-badge--error"
                      : quizHasLearningContent
                      ? "edit-quiz-status-badge--ready"
                      : "edit-quiz-status-badge--empty"
                  }`}
                >
                  {learningContentLoadError
                    ? "Unavailable"
                    : quizHasLearningContent
                    ? `${learningContentBlocks.length} block${learningContentBlocks.length === 1 ? "" : "s"}`
                    : "Not added"}
                </span>
              </div>
              <p className="edit-quiz-learning-description">
                Add optional material students can review before starting the assessment, including text, links, images and PDF resources.
              </p>
            </div>
            <button
              type="button"
              className="edit-quiz-button edit-quiz-button--primary edit-quiz-learning-action"
              onClick={() => {
                setContentMessage(null);
                setShowLearningContentBuilder(true);
              }}
              disabled={Boolean(learningContentLoadError) || contentSaving}
            >
              {quizHasLearningContent ? "Edit Learning Content" : "Add Learning Content"}
            </button>
          </div>

        {learningContentLoadError && (
          <div className="learning-content-management-error" role="alert">
            <strong>Learning content is unavailable.</strong> {learningContentLoadError} Nothing was changed.
          </div>
        )}
        {contentMessage && (
          <div
            className={`learning-content-management-message ${contentMessage.type}`}
            role={contentMessage.type === "error" ? "alert" : "status"}
          >
            {contentMessage.text}
          </div>
        )}
        </section>

        {/* ══ QUESTION ORDER ═════════════════════════════════════════════════ */}
        {/* Header row: title + Add Questions button */}
        <section className="edit-quiz-section edit-quiz-questions-section" aria-labelledby="edit-quiz-questions-title">
        <div className="edit-quiz-questions-header">
          <div>
            <p className="edit-quiz-section-kicker">Assessment flow</p>
            <div className="edit-quiz-title-with-count">
              <h2 id="edit-quiz-questions-title">Question Order</h2>
              <span>
                {questions.length} question{questions.length !== 1 ? "s" : ""} · {formatQuizMarkCount(totalQuizMarks)}
              </span>
            </div>
            <p className="edit-quiz-questions-help">Arrange the sequence students will see during the test.</p>
          </div>
          <div className="edit-quiz-question-actions">
            <span className="edit-quiz-reorder-hint">Use ↑ ↓ to reorder</span>
            <button
              type="button"
              className="edit-quiz-button edit-quiz-button--tertiary"
              onClick={() => {
                setQuestions((prev) =>
                  [...prev].sort((a, b) =>
                    naturalSortByLabel(a.questionLabel || "", b.questionLabel || "")
                  )
                );
                setSaveMessage({ type: "success", text: "✅ Questions reordered by label. Click Save Changes to persist." });
                setTimeout(() => setSaveMessage(null), 5000);
              }}
              title="Automatically sort questions into natural exam order by question label"
            >
              ↕ Reorder Questions
            </button>
            <button
              type="button"
              className="edit-quiz-button edit-quiz-button--primary"
              onClick={openAddModal}
            >
              + Add Questions
            </button>
          </div>
        </div>

        {questions.length === 0 ? (
          <div className="edit-quiz-empty-questions">
            <p>No questions in this test yet. Click <strong>+ Add Questions</strong> to add from your Question Bank.</p>
          </div>
        ) : (
          <div className="edit-quiz-question-list">
            {questions.map((q, idx) => (
              <article key={q.id} className="edit-quiz-question-item">
                {/* Order badge */}
                <div className="edit-quiz-order-badge" aria-label={`Question position ${idx + 1}`}>{idx + 1}</div>

                {/* Question info */}
                <div className="edit-quiz-question-info">
                  <div className="edit-quiz-question-label">
                    {q.questionLabel || `Q${idx + 1}`}
                  </div>
                   <div className="edit-quiz-question-preview">
                    {q.questionText
                      ? q.questionText.replace(/\n/g, " ").substring(0, 120) +
                        (q.questionText.length > 120 ? "…" : "")
                      : (q.questionImageURL || q.questionMedia)
                      ? "[Image question]"
                       : "No text"}
                   </div>
                   <QuizMcqPreview question={q} showOptions />
                 </div>

                {/* Marks */}
                <div className="edit-quiz-marks-badge">{q.maxMark ?? "?"} mk</div>

                {/* Move buttons */}
                <button
                  type="button"
                  className="edit-quiz-icon-button"
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="edit-quiz-icon-button"
                  onClick={() => moveDown(idx)}
                  disabled={idx === questions.length - 1}
                  title="Move down"
                >
                  ↓
                </button>

                {/* Remove from quiz (does NOT delete from Firestore) */}
                <button
                  type="button"
                  className="edit-quiz-remove-button"
                  onClick={() => handleRemoveFromQuiz(q.id)}
                  title="Remove from this test (question stays in your Question Bank)"
                >
                  Remove
                </button>
              </article>
            ))}
          </div>
        )}

        </section>

        {/* ── Save button ── */}
        <div className="edit-quiz-bottom-actions">
          <button
            type="button"
            className="edit-quiz-button edit-quiz-button--save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button
            type="button"
            className="edit-quiz-button edit-quiz-button--secondary"
            onClick={() => navigate("/view-quizzes")}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* ══ ADD QUESTIONS MODAL ══════════════════════════════════════════════ */}
      {showAddModal && (
        <div
          className="edit-quiz-modal-backdrop"
          onClick={() => setShowAddModal(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setShowAddModal(false);
          }}
        >
          <div
            className="edit-quiz-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-quiz-add-questions-title"
            aria-describedby="edit-quiz-add-questions-description"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="edit-quiz-modal-header">
              <div>
                <p className="edit-quiz-section-kicker">Question Bank</p>
                <h2 id="edit-quiz-add-questions-title">Add Questions</h2>
                <p id="edit-quiz-add-questions-description">Select questions to append to the current test order.</p>
              </div>
              <button
                type="button"
                className="edit-quiz-modal-close"
                aria-label="Close Add Questions dialog"
                autoFocus
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>

            {/* Search + filters */}
            <div className="edit-quiz-modal-filters">
              <label className="edit-quiz-visually-hidden" htmlFor="edit-quiz-bank-search">Search question bank</label>
              <input
                id="edit-quiz-bank-search"
                className="edit-quiz-modal-search"
                type="text"
                placeholder="Search by label, question text, topic or subject…"
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
              />
              <div className="edit-quiz-modal-filter-grid">
                <label className="edit-quiz-visually-hidden" htmlFor="edit-quiz-bank-subject">Filter by subject</label>
                <select
                  id="edit-quiz-bank-subject"
                  value={bankFilters.subject}
                  onChange={(e) => setBankFilters((p) => ({ ...p, subject: e.target.value }))}
                >
                  <option value="">All subjects</option>
                  {bankSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="edit-quiz-visually-hidden" htmlFor="edit-quiz-bank-topic">Filter by topic</label>
                <input id="edit-quiz-bank-topic" type="text" placeholder="Topic…" value={bankFilters.topic}
                  onChange={(e) => setBankFilters((p) => ({ ...p, topic: e.target.value }))}
                />
                <label className="edit-quiz-visually-hidden" htmlFor="edit-quiz-bank-level">Filter by level</label>
                <input id="edit-quiz-bank-level" type="text" placeholder="Level…" value={bankFilters.level}
                  onChange={(e) => setBankFilters((p) => ({ ...p, level: e.target.value }))}
                />
                <label className="edit-quiz-visually-hidden" htmlFor="edit-quiz-bank-spec">Filter by specification code</label>
                <input id="edit-quiz-bank-spec" type="text" placeholder="Spec code…" value={bankFilters.specCode}
                  onChange={(e) => setBankFilters((p) => ({ ...p, specCode: e.target.value }))}
                />
              </div>
            </div>

            {/* Question list (scrollable) */}
            <div className="edit-quiz-modal-list">
              {bankLoading ? (
                <p className="edit-quiz-modal-empty" role="status">Loading question bank…</p>
              ) : filteredBankQuestions.length === 0 ? (
                <p className="edit-quiz-modal-empty">
                  {bankQuestions.length === 0 ? "No questions found in your Question Bank." : "No questions match your search."}
                </p>
              ) : (
                filteredBankQuestions.map((q) => {
                  const alreadyInQuiz = currentQuizIds.has(q.id);
                  const selected = selectedBankIds.has(q.id);
                  return (
                    <div
                      key={q.id}
                      className={`edit-quiz-bank-question ${alreadyInQuiz ? "edit-quiz-bank-question--added" : ""} ${selected ? "edit-quiz-bank-question--selected" : ""}`}
                      onClick={() => !alreadyInQuiz && toggleBankSelect(q.id)}
                    >
                      {/* Checkbox */}
                      <div className="edit-quiz-bank-question-check">
                        {alreadyInQuiz ? (
                          <span className="edit-quiz-bank-added-icon" title="Already in this test">✅</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleBankSelect(q.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>

                      {/* Question content */}
                      <div className="edit-quiz-bank-question-content">
                       <div className="edit-quiz-bank-question-meta">
                          <span className="edit-quiz-bank-question-label">
                            {q.questionLabel || "—"}
                          </span>
                          <span className="edit-quiz-bank-tag edit-quiz-bank-tag--marks">
                            {q.maxMark ?? "?"} mk
                          </span>
                          {q.subject && (
                            <span className="edit-quiz-bank-tag">
                              {q.subject}
                            </span>
                          )}
                          {q.topic && (
                            <span className="edit-quiz-bank-tag edit-quiz-bank-tag--topic">
                              {q.topic}
                            </span>
                          )}
                          {alreadyInQuiz && (
                            <span className="edit-quiz-bank-tag edit-quiz-bank-tag--added">
                              Already added
                            </span>
                          )}
                        </div>
                        <QuizMcqPreview question={q} />
                        <div className="edit-quiz-bank-question-preview">
                          {q.questionText
                            ? q.questionText.replace(/\n/g, " ").substring(0, 150) + (q.questionText.length > 150 ? "…" : "")
                            : (q.questionImageURL || q.questionMedia) ? "[Image question]" : "No text"}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal footer */}
            <div className="edit-quiz-modal-footer">
              <span className="edit-quiz-modal-selection-count">
                {selectedBankIds.size > 0
                  ? `${selectedBankIds.size} question${selectedBankIds.size !== 1 ? "s" : ""} selected`
                  : "Click questions to select them"}
              </span>
              <div className="edit-quiz-modal-footer-actions">
                <button
                  type="button"
                  className="edit-quiz-button edit-quiz-button--secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="edit-quiz-button edit-quiz-button--save"
                  onClick={handleAddToQuiz}
                  disabled={selectedBankIds.size === 0}
                >
                  Add Selected ({selectedBankIds.size})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default EditQuiz;
